import { useState } from "react";
import { DCUIContent, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { DCUIThemingThemeValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingPreviewContainer } from "../../../../common/dcui-theming-preview-container/dcui-theming-preview-container";
import { DCUIThemingThemeHeaderSection } from "./dcui-theming-theme-header-section/dcui-theming-theme-header-section";
import { DCUIThemingThemeGeneralSection } from "./dcui-theming-theme-general-section/dcui-theming-theme-general-section";
import { DCUIThemingThemeHeaderTextSection } from "./dcui-theming-theme-header-text-section/dcui-theming-theme-header-text-section";
import { DCUIThemingThemeHeaderLogoSection } from "./dcui-theming-theme-header-logo-section/dcui-theming-theme-header-logo-section";
import { DCUIThemingThemeFooterSection } from "./dcui-theming-theme-footer-section/dcui-theming-theme-footer-section";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingThemeTabContainerProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingThemeValidationErrors;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    inheritedDCUIContent: DCUIContent;
    inheritedDCUITheme: DCUITheme;
    pageLevel: PageLevelTypes;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
    updateDCUIThemePropertyValue: (dcuiTheme: DCUITheme) => void;
};

export const DCUIThemingThemeTabContainer = (props: DCUIThemingThemeTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        dcuiContent,
        dcuiTheme,
        inheritedDCUIContent,
        inheritedDCUITheme,
        pageLevel,
        documentCaptureSteps,
        previewEntityType,
        uploadFileToS3,
        updateDCUIContentPropertyValue,
        updateDCUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const themeResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme;

    return (
        <div>
            <ContentPageHeader title={themeResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={themeResources.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection title={themeResources.sections.header.title} showSeparator={true}>
                            <DCUIThemingThemeHeaderSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                dcuiTheme={dcuiTheme}
                                inheritedDCUITheme={inheritedDCUITheme}
                                pageLevel={pageLevel}
                                updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                            />
                            <DCUIThemingThemeHeaderLogoSection
                                dcuiTheme={dcuiTheme}
                                inheritedDCUITheme={inheritedDCUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                uploadFileToS3={uploadFileToS3}
                                updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                            />
                            <DCUIThemingThemeHeaderTextSection
                                dcuiContent={dcuiContent}
                                dcuiTheme={dcuiTheme}
                                inheritedDCUIContent={inheritedDCUIContent}
                                inheritedDCUITheme={inheritedDCUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                                updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection title={themeResources.sections.footer.title} showSeparator={true}>
                            <DCUIThemingThemeFooterSection
                                dcuiContent={dcuiContent}
                                dcuiTheme={dcuiTheme}
                                inheritedDCUIContent={inheritedDCUIContent}
                                inheritedDCUITheme={inheritedDCUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                                updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection title={themeResources.sections.general.title} showSeparator={false}>
                            <DCUIThemingThemeGeneralSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                dcuiTheme={dcuiTheme}
                                inheritedDCUITheme={inheritedDCUITheme}
                                pageLevel={pageLevel}
                                updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                            />
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    <DCUIThemingPreviewContainer
                        currentTab={DCUIThemingTabTypes.THEME}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={previewEntityType}
                    />
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};